import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row
} from 'reactstrap';
import { toast } from 'react-toastify';
import PageHeader from '../common/PageHeader';
import CollapsibleFilterSection from '../common/CollapsibleFilterSection';
import QuillEditor from '../common/QuillEditor';
import ActionConfirmModal from '../common/ActionConfirmModal';
import TablePageSizeSelect from '../common/TablePageSizeSelect';
import MedDataTable from '../common/MedDataTable';
import apiBase from '../../apiConfig';
import useStoredTablePageSize from '../../hooks/useStoredTablePageSize';

const uzdSections = [
  {
    key: 'templates',
    path: '/uzd/templates',
    label: 'Shablonlar',
    accent: 'primary',
    statLabel: 'shablon'
  },
  {
    key: 'queue',
    path: '/uzd/queue',
    label: 'UZI navbati',
    accent: 'warning',
    statLabel: 'bemorlar'
  }
];

const initialTemplateForm = {
  name: '',
  text: '',
  is_active: 1,
  section_ids: []
};

const hasText = value => String(value || '').trim() !== '';

const templatePreview = value => {
  const plainText = String(value || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (plainText.length <= 140) {
    return plainText || '-';
  }

  return `${plainText.slice(0, 140).trim()}...`;
};

const Uzd = () => {
  const [templateSearch, setTemplateSearch] = useState('');
  const [templates, setTemplates] = useState([]);
  const [uzdTemplateSections, setUzdTemplateSections] = useState([]);
  const [templateForm, setTemplateForm] = useState(initialTemplateForm);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [isTemplateSaving, setIsTemplateSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [queuePatients, setQueuePatients] = useState([]);
  const [filials, setFilials] = useState([]);
  const [queueSearch, setQueueSearch] = useState('');
  const [queueFilialId, setQueueFilialId] = useState('');
  const [pageSize, setPageSize] = useStoredTablePageSize();
  const [activeSectionKey, setActiveSectionKey] = useState('templates');
  const activeSection = uzdSections.find(section => section.key === activeSectionKey) || uzdSections[0];

  const fetchTemplates = async () => {
    const res = await axios.get(`${apiBase}/uzd_templates.php`, {
      params: { search: templateSearch }
    });

    if (res.data.success) {
      setTemplates(res.data.templates || []);
      setUzdTemplateSections(res.data.sections || []);
    }
  };

  const fetchQueue = async () => {
    const [patientsRes, filialsRes] = await Promise.all([
      axios.get(`${apiBase}/patients.php`, {
        params: {
          search: queueSearch,
          filial_id: queueFilialId || undefined,
          limit: 500
        }
      }),
      axios.get(`${apiBase}/reference.php`, { params: { type: 'filials' } })
    ]);

    if (patientsRes.data.success) {
      setQueuePatients((patientsRes.data.patients || []).filter(patient => hasText(patient.doctor)));
    }

    if (filialsRes.data.success) {
      setFilials(filialsRes.data.items || []);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchTemplates().catch(error => {
        toast.error(error.response?.data?.message || 'UZD shablonlari yuklanmadi');
      });
    }, 180);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateSearch]);

  useEffect(() => {
    fetchQueue().catch(error => {
      toast.error(error.response?.data?.message || 'UZI navbati yuklanmadi');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueSearch, queueFilialId]);

  const resetTemplateForm = () => {
    setEditingTemplateId(null);
    setTemplateForm(initialTemplateForm);
  };

  const closeTemplateModal = () => {
    setTemplateModalOpen(false);
    resetTemplateForm();
  };

  const openTemplateCreate = () => {
    resetTemplateForm();
    setTemplateModalOpen(true);
  };

  const openTemplateEdit = template => {
    setEditingTemplateId(template.id);
    setTemplateForm({
      name: template.name || '',
      text: template.text || '',
      is_active: template.is_active ? 1 : 0,
      section_ids: (template.sections || []).map(section => Number(section.id))
    });
    setTemplateModalOpen(true);
  };

  const toggleTemplateSection = sectionId => {
    setTemplateForm(form => ({
      ...form,
      section_ids: form.section_ids.includes(sectionId)
        ? form.section_ids.filter(item => item !== sectionId)
        : [...form.section_ids, sectionId]
    }));
  };

  const handleTemplateSubmit = async e => {
    e.preventDefault();
    setIsTemplateSaving(true);

    try {
      const payload = {
        name: templateForm.name.trim(),
        text: templateForm.text,
        is_active: Number(templateForm.is_active),
        section_ids: templateForm.section_ids
      };

      if (editingTemplateId) {
        await axios.put(`${apiBase}/uzd_templates.php`, { ...payload, id: editingTemplateId });
        toast.success('Shablon yangilandi');
      } else {
        await axios.post(`${apiBase}/uzd_templates.php`, payload);
        toast.success('Shablon saqlandi');
      }

      closeTemplateModal();
      fetchTemplates().catch(console.error);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Shablonni saqlashda xatolik yuz berdi');
    } finally {
      setIsTemplateSaving(false);
    }
  };

  const handleTemplateDelete = async () => {
    if (!deleteTarget) {
      return;
    }

    try {
      const res = await axios.delete(`${apiBase}/uzd_templates.php`, {
        data: { id: deleteTarget.id }
      });

      toast.success(res.data?.message || "Shablon o'chirildi");
      if (editingTemplateId === deleteTarget.id) {
        closeTemplateModal();
      }
      setDeleteTarget(null);
      fetchTemplates().catch(console.error);
    } catch (error) {
      toast.error(error.response?.data?.message || "Shablonni o'chirishda xatolik yuz berdi");
    }
  };

  const sectionMetrics = {
    templates: templates.length,
    queue: queuePatients.length
  };

  const templateColumns = [
    { dataField: 'name', text: 'Nomi', sort: true, classes: 'align-middle font-weight-bold' },
    { dataField: 'text', text: 'Matn', sort: true, classes: 'align-middle text-600', formatter: value => templatePreview(value) },
    {
      dataField: 'sections_text',
      text: "Bo'limlar",
      sort: true,
      classes: 'align-middle',
      formatter: (value, item) => (
        <div className="med-uzd-table-section-list">
          {(item.sections || []).map(section => (
            <Badge key={`${item.id}-${section.id}`} color="soft-info" pill>
              {section.title}
            </Badge>
          ))}
        </div>
      )
    },
    {
      dataField: 'is_active',
      text: 'Holat',
      sort: true,
      classes: 'align-middle',
      formatter: value => <Badge color={value ? 'soft-success' : 'soft-secondary'}>{value ? 'Faol' : 'Nofaol'}</Badge>
    },
    {
      dataField: 'actions',
      text: 'Amallar',
      isDummyField: true,
      headerStyle: { width: '6.5rem' },
      classes: 'align-middle',
      formatter: (data, item) => (
        <div className="med-actions-row">
          <Button type="button" color="link" className="p-0 med-action-btn" onClick={() => openTemplateEdit(item)}>
            <FontAwesomeIcon icon="pencil-alt" />
          </Button>
          <Button
            type="button"
            color="link"
            className="p-0 med-action-btn med-action-btn-danger"
            onClick={() => setDeleteTarget({ id: item.id, name: item.name })}
          >
            <FontAwesomeIcon icon="trash-alt" />
          </Button>
        </div>
      )
    }
  ];

  const queueColumns = [
    { dataField: 'id', text: 'ID', sort: true, classes: 'align-middle' },
    { dataField: 'full_name', text: 'F.I.O', sort: true, classes: 'align-middle font-weight-bold' },
    { dataField: 'date_birth', text: "Tug'ilgan sana", sort: true, classes: 'align-middle', formatter: value => value || '-' },
    { dataField: 'filial_name', text: 'Filial', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    { dataField: 'doctor', text: 'Doktor', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    { dataField: 'phone', text: 'Telefon', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    {
      dataField: 'debt',
      text: 'Qarz',
      sort: true,
      classes: 'align-middle',
      formatter: value => <Badge color={Number(value) > 0 ? 'soft-danger' : 'soft-success'}>{Number(value || 0).toLocaleString('ru-RU')}</Badge>
    }
  ];

  return (
    <>
      <PageHeader
        title="UZD"
        description="Kabinet UZD uchun shablonlar va UZI navbatini shu bo'limda boshqarish mumkin."
        className="mb-3"
      />

      <Card className="border-0 shadow-sm med-dashboard-card med-patient-filter-card mb-3">
        <CardBody className="med-dashboard-card-body">
          <CollapsibleFilterSection
            title="UZD bo'limlari"
            summary={`${activeSection.label} bo'limi tanlangan`}
            actions={
              <Badge color={`soft-${activeSection.accent}`} pill>
                {activeSection.label}: {sectionMetrics[activeSection.key] || 0}
              </Badge>
            }
            storageKey="uzd-sections-collapse"
          >
            <div className="med-patient-filter-grid med-uzd-filter-grid">
              {uzdSections.map(section => (
                <button
                  key={section.key}
                  type="button"
                  className={`med-patient-filter-item med-patient-filter-item-${section.accent}${
                    activeSection.key === section.key ? ' is-active' : ''
                  }`}
                  onClick={() => setActiveSectionKey(section.key)}
                >
                  <span className="med-patient-filter-label">{section.label}</span>
                  <span className="med-patient-filter-meta">
                    <strong>{sectionMetrics[section.key] || 0}</strong>
                    <small>{section.statLabel}</small>
                  </span>
                </button>
              ))}
            </div>
          </CollapsibleFilterSection>
        </CardBody>
      </Card>

      {activeSection.key === 'templates' ? (
        <>
          <Card className="border-0 shadow-sm med-dashboard-card">
            <CardBody className="med-dashboard-card-body">
              <CollapsibleFilterSection
                title="Shablon filtrlari"
                summary={templateSearch ? `Qidiruv: ${templateSearch}` : "Barcha UZD shablonlari"}
                actions={
                  <Badge color="soft-primary" pill>
                    UZD shablonlari: {templates.length}
                  </Badge>
                }
                storageKey="uzd-template-filters-collapse"
              >
                <Row className="mb-3 align-items-center">
                  <Col md="auto" className="mb-3 mb-md-0">
                    <TablePageSizeSelect value={pageSize} onChange={setPageSize} className="med-table-page-size" />
                  </Col>
                  <Col md={5} className="mb-3 mb-md-0">
                    <Input
                      value={templateSearch}
                      onChange={e => setTemplateSearch(e.target.value)}
                      placeholder="Shablon nomi yoki bo'limi bo'yicha qidirish"
                      className="bg-200 border-0"
                    />
                  </Col>
                  <Col className="text-md-right">
                    <div className="d-flex flex-wrap justify-content-md-end align-items-center">
                      <Button type="button" color="outline-primary" className="med-outline-add-btn" onClick={openTemplateCreate}>
                        <FontAwesomeIcon icon="plus" className="mr-2" />
                        Yangi shablon
                      </Button>
                    </div>
                  </Col>
                </Row>
              </CollapsibleFilterSection>

              <MedDataTable
                data={templates.map(item => ({
                  ...item,
                  sections_text: (item.sections || []).map(section => section.title).join(', ')
                }))}
                columns={templateColumns}
                pageSize={pageSize}
                paginationResetKey={`templates-${templateSearch}`}
                noDataIndication={<div className="med-dashboard-empty">UZD shablonlari topilmadi.</div>}
              />
            </CardBody>
          </Card>
        </>
      ) : (
        <Card className="border-0 shadow-sm med-dashboard-card">
          <CardBody className="med-dashboard-card-body">
            <CollapsibleFilterSection
              title="Navbat filtrlari"
              summary={queueSearch ? `Qidiruv: ${queueSearch}` : "Barcha UZI navbati"}
              storageKey="uzd-queue-filters-collapse"
            >
              <Row className="mb-3 align-items-center">
                <Col md="auto" className="mb-3 mb-md-0">
                  <TablePageSizeSelect value={pageSize} onChange={setPageSize} className="med-table-page-size" />
                </Col>
                <Col md={4} className="mb-3 mb-md-0">
                  <Input
                    value={queueSearch}
                    onChange={e => setQueueSearch(e.target.value)}
                    placeholder="Bemor F.I.O, telefon yoki ID bo'yicha qidirish"
                    className="bg-200 border-0"
                  />
                </Col>
                <Col md={3}>
                  <Input
                    type="select"
                    value={queueFilialId}
                    onChange={e => setQueueFilialId(e.target.value)}
                    className="bg-200 border-0"
                  >
                    <option value="">Barcha filiallar</option>
                    {filials.map(item => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </Input>
                </Col>
              </Row>
            </CollapsibleFilterSection>

            <MedDataTable
              data={queuePatients}
              columns={queueColumns}
              pageSize={pageSize}
              paginationResetKey={`queue-${queueSearch}-${queueFilialId}`}
              noDataIndication={<div className="med-dashboard-empty">UZI navbatida bemorlar topilmadi.</div>}
            />
        </CardBody>
      </Card>
      )}

      <ActionConfirmModal
        isOpen={Boolean(deleteTarget)}
        toggle={() => setDeleteTarget(null)}
        title="Shablonni o'chirish"
        message={deleteTarget ? `${deleteTarget.name} shablonini o'chirmoqchimisiz?` : ''}
        confirmLabel="O'chirish"
        onConfirm={handleTemplateDelete}
      />

      <Modal isOpen={templateModalOpen} toggle={closeTemplateModal} size="xl" centered className="med-confirm-modal">
        <ModalHeader toggle={closeTemplateModal}>
          {editingTemplateId ? 'Shablonni tahrirlash' : 'Yangi shablon'}
        </ModalHeader>
        <Form onSubmit={handleTemplateSubmit} className="med-uzd-template-form">
          <ModalBody>
            <Row form>
              <Col md={8}>
                <FormGroup>
                  <Label>Shablon nomi</Label>
                  <Input
                    value={templateForm.name}
                    onChange={e => setTemplateForm(form => ({ ...form, name: e.target.value }))}
                    placeholder="Masalan: Jigar UZI xulosasi"
                    className="bg-200 border-0"
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Holat</Label>
                  <Input
                    type="select"
                    value={templateForm.is_active}
                    onChange={e => setTemplateForm(form => ({ ...form, is_active: Number(e.target.value) }))}
                    className="bg-200 border-0"
                  >
                    <option value={1}>Faol</option>
                    <option value={0}>Nofaol</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <FormGroup className="mb-3">
              <Label>Matn</Label>
              <div className="med-uzd-editor-wrap">
                <QuillEditor
                  value={templateForm.text}
                  onChange={value => setTemplateForm(form => ({ ...form, text: value }))}
                  className="med-uzd-editor"
                />
              </div>
            </FormGroup>

            <div className="d-flex flex-wrap align-items-center justify-content-between mb-2">
              <div>
                <h6 className="mb-1">UZD bo'limlari</h6>
                <small className="text-500">Tanlangan bo'limlar shu shablon ostida ishlaydi.</small>
              </div>
              <Badge color="soft-primary" pill>
                Tanlandi: {templateForm.section_ids.length}
              </Badge>
            </div>

            <div className="med-uzd-template-section-grid">
              {uzdTemplateSections.map(section => {
                const isSelected = templateForm.section_ids.includes(section.id);
                return (
                  <button
                    key={section.id}
                    type="button"
                    className={`med-uzd-template-section${isSelected ? ' is-active' : ''}${
                      section.active ? '' : ' is-inactive'
                    }`}
                    onClick={() => toggleTemplateSection(section.id)}
                  >
                    <span className="med-uzd-template-section-title">{section.title}</span>
                    <span className="med-uzd-template-section-meta">
                      {Number(section.price || 0).toLocaleString('ru-RU')}
                    </span>
                  </button>
                );
              })}
            </div>

            {uzdTemplateSections.length === 0 && <div className="med-dashboard-empty mt-3">UZD bo'limlari topilmadi.</div>}
          </ModalBody>
          <ModalFooter>
            <Button type="button" color="light" onClick={closeTemplateModal}>
              Bekor qilish
            </Button>
            <Button color="primary" type="submit" disabled={isTemplateSaving}>
              <FontAwesomeIcon icon={editingTemplateId ? 'check-circle' : 'plus'} className="mr-2" />
              {isTemplateSaving ? 'Saqlanmoqda...' : editingTemplateId ? 'Yangilash' : 'Saqlash'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </>
  );
};

export default Uzd;
